import React, { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";
import { MapHomeControl, MapLegendControl } from "../../../../common/MapControls/MapControls";

const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

const InfoButton = ({ onClick }) => {
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  return (
    <div
      ref={btnRef}
      style={{
        position: "absolute",
        bottom: "20px",
        left: "12px",
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        //boxShadow: "0 0 6px rgba(0,0,0,0.3)",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        zIndex: 1000,
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};

const SoilData = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [soilData, setSoilData] = useState(null); // Solos Port LL data
  const [legendVisible, setLegendVisible] = useState(false);
  const legendRef = useRef(null);
     const [info, setInfo] = useState(false);
     const infoRef = useRef(null);
     const infoPanelRef = useCallback((node) => {
       infoRef.current = node;
       if (node) {
         L.DomEvent.disableClickPropagation(node);
         L.DomEvent.disableScrollPropagation(node);
       }
     }, []);

  useEffect(() => {
    // Load all GeoJSON files, excluding bathymetry data
    Promise.all([
      fetch("/DATA/OSMbased_adm0_boundary.json").then((res) => res.json()),
      fetch("/DATA/Landslide/Geological & Terrain Data/Solos_port_LL.json").then((res) =>
        res.json()
      ),
    ])
      .then(([boundary, soil]) => {
        setBoundaryData(boundary);
        setSoilData(soil);
      })
      .catch((error) => console.error("Error loading GeoJSON data:", error));
  }, []);

  // const toggleLegend = () => {
    setLegendVisible(!legendVisible);
  // };

  // Close legend when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (legendRef.current && !legendRef.current.contains(event.target)) {
        setLegendVisible(false);
      }
    };

    if (legendVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [legendVisible]);

  // Function to handle popups for each feature
  const onEachFeature = (feature, layer) => {
    if (feature.properties) {
      const popupContent = Object.keys(feature.properties)
        .map((key) => `<b>${key}</b>: ${feature.properties[key]}`)
        .join("<br />");

      layer.bindPopup(popupContent);
    }
  };

  return (
    <div className="wind-speed-cyclone-container">
      <div className="map-container-WindSpeedCyclone">
        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          className="mapcontainerall"
        >
          <MapHomeControl />
                              {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              The Soil data information are available here.
            </div>
          )}
          <InfoButton onClick={() => setInfo(!info)} />
          {/* Layer toggle control */}
          <LayersControl position="topright">
            {/* Base Layers */}
            <LayersControl.BaseLayer name="White Theme" checked>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="OpenStreetMap">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>

            {/* GeoJSON Layers */}
            {boundaryData && (
              <LayersControl.Overlay name="Country Boundary" checked>
                <GeoJSON
                  data={boundaryData}
                  style={{
                    color: "#000000",
                    weight: 2,
                    opacity: 0.8,
                    fillOpacity: 0,
                  }}
                  onEachFeature={onEachFeature}
                />
              </LayersControl.Overlay>
            )}
            {soilData && (
              <LayersControl.Overlay name="Soil Data (Solos Port LL)" checked>
                <GeoJSON
                  data={soilData}
                  style={{
                    color: "#8B4513",
                    weight: 1,
                    opacity: 0.8,
                    fillOpacity: 0.5,
                  }}
                  onEachFeature={onEachFeature}
                />
              </LayersControl.Overlay>
            )}
          </LayersControl>

          {/* Legend */}
          <MapLegendControl
            sections={[{
              shape: "square",
              items: [
                { label: "Country Boundary", color: "#000000" },
                { label: "Soil Data (Solos Port LL)", color: "#8B4513" },
              ],
            }]}
          />
        </MapContainer>
      </div>
    </div>
  );
};

export default SoilData;